import {
    Component,
    OnInit,
    ViewChild,
    Input,
    OnDestroy,
    ElementRef,
    EventEmitter,
    Output,
    ViewEncapsulation,
    Inject,
    AfterViewInit,
    ChangeDetectorRef,
    inject,
} from "@angular/core";
import {
    mergeMap as _observableMergeMap,
    Observable,
    of as _observableOf,
    Subject,
    throwError as _observableThrow,
} from "rxjs";
import { AngularCropperjsModule, CropperComponent, ImageCropperResult, ImageCropperSetting } from "angular-cropperjs";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { NgxFileDropEntry, NgxFileDropModule } from "ngx-file-drop";
import { ApiException, LEGACY_API_BASE_URL, UploadTemporalFileResponse } from "@api";
import { catchError as _observableCatch } from "rxjs/internal/operators/catchError";
import { HttpClient, HttpResponse, HttpResponseBase } from "@angular/common/http";
import { NotifyService } from "@ui/notify";
import { TranslocoCoreModule } from "@i18n";
import { MatIcon } from "@angular/material/icon";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatDividerModule } from "@angular/material/divider";
import { TranslocoService } from "@jsverse/transloco";

@Component({
    selector: "image-editor-dialog",
    templateUrl: "./image-editor-dialog.component.html",
    styleUrls: ["./image-editor-dialog.component.scss"],
    encapsulation: ViewEncapsulation.None,
    imports: [
        TranslocoCoreModule,
        MatDialogContent,
        MatIcon,

        CommonModule,
        MatButtonModule,
        MatTooltipModule,
        MatToolbarModule,
        MatDialogModule,
        AngularCropperjsModule,
        MatProgressSpinnerModule,
        MatDividerModule,

        NgxFileDropModule,
    ],
    standalone: true,
})
export class ImageEditorDialogComponent implements OnInit, OnDestroy, AfterViewInit {
    protected _unsubscribeAll: Subject<any>;

    protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

    private _notify: NotifyService = inject(NotifyService);

    title: string;

    @ViewChild("imageFileInput")
    imageFileInput: ElementRef;

    @ViewChild("angularCropper") public angularCropper: CropperComponent;
    config = {
        dragMode: "move",
        background: true,
        movable: true,
        rotatable: true,
        scalable: true,
        zoomable: true,
        viewMode: 1,
        checkImageOrigin: true,
        checkCrossOrigin: true,
    };

    @Input() acceptedFilesMimetypes: string = "image/png,image/jpeg,image/jpg,.png,.jpg,.jpeg";

    mimeType: string = "image/png";

    @Input() imageUrl: SafeUrl;

    plainImageUrl: string;

    @ViewChild("image") image: ElementRef;

    imageUploaded: boolean;

    uploadingImage: boolean;

    imageEditorReady: boolean;

    scaleX: number = 1;

    scaleY: number = 1;

    @Input() settings: ImageCropperSetting;

    export = new EventEmitter<ImageCropperResult>();

    @Output() exportResult = new EventEmitter<SafeUrl>();

    openExplorerOnStartup: boolean;

    baseUrl: string;

    constructor(
        public matDialogRef: MatDialogRef<ImageEditorDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private _data: any,
        private _sanitizer: DomSanitizer,
        private _httpClient: HttpClient,
        private _changeDetector: ChangeDetectorRef,
        private _transloco: TranslocoService,
        @Inject(LEGACY_API_BASE_URL) private _baseUrl?: string
    ) {
        this._unsubscribeAll = new Subject();
        this.baseUrl = this._baseUrl;

        this.title = this._transloco.translate("Editar imagem"); // Use transloco
        if (_data) {
            if (_data.title) {
                this.title = _data.title;
            }
            if (_data.settings) {
                this.settings = _data.settings;
            }
            if (_data.openExplorerOnStartup) {
                this.openExplorerOnStartup = _data.openExplorerOnStartup;
            }
            if (_data.droppedFile) {
                this.files = [_data.droppedFile];
            }
            if (_data.accept) {
                this.acceptedFilesMimetypes = _data.accept;
            }
        }
        this.matDialogRef.addPanelClass("image-editor-dialog");
    }

    ngOnInit() {
        // this.settings = {width: 1024, height: 1024};
        this.imageUploaded = !!this.imageUrl;
        this.export.subscribe((result: ImageCropperResult) => {
            let originalFile = this.plainImageUrl.replace(/[\#\?].*$/, "");
            this.uploadBlob(result.blob, originalFile).subscribe(
                (result) => {
                    //this._fuseProgress.hide();
                    this.uploadingImage = false;
                    this.exportResult.emit(this._sanitizer.bypassSecurityTrustUrl(result.url));
                    this.matDialogRef.close({ url: result.url });
                },
                (error1) => {
                    //this._fuseProgress.hide();
                }
            );
        });
    }

    ngAfterViewInit(): void {
        if (this.openExplorerOnStartup) {
            this.onOpenFileExplorer();
        }
        if (this.files && this.files.length > 0) {
            this.dropped(this.files);
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    onImageLoaded(ready) {
        this.imageEditorReady = ready;
        if (ready) {
            this.uploadingImage = false;
        }
    }

    fileChangeEvent(event: any): void {
        this.uploadFile(event.target.files[0]);
    }

    rotateLeft() {
        this.angularCropper.cropper.rotate(-90);
    }

    rotateRight() {
        this.angularCropper.cropper.rotate(90);
    }

    flipHorizontal() {
        this.scaleX *= -1;
        this.angularCropper.cropper.scale(this.scaleX, this.scaleY);
    }

    flipVertical() {
        this.scaleY *= -1;
        this.angularCropper.cropper.scale(this.scaleX, this.scaleY);
    }

    zoomOut() {
        this.angularCropper.cropper.zoom(-0.1);
    }

    zoomIn() {
        this.angularCropper.cropper.zoom(0.1);
    }

    resetImage() {
        this.angularCropper.cropper.reset();
    }

    onOpenFileExplorer(event?: any): void {
        if (event) {
            event.preventDefault();
        }
        this.imageFileInput.nativeElement.click();
    }

    cropAndSaveSave() {
        //this._fuseProgress.show();
        this.uploadingImage = true;
        this.exportCanvas(false, {});
    }

    public files: NgxFileDropEntry[] = [];

    public dropped(files: NgxFileDropEntry[]) {
        this.files = files;
        if (files && files.length > 0) {
            let droppedFile = files[0];
            // Is it a file?
            if (
                droppedFile.fileEntry.isFile &&
                this.acceptedFilesMimetypes
                    .split(",")
                    .findIndex((i) => droppedFile.fileEntry.name.toLowerCase().endsWith(i)) >= 0
            ) {
                const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
                fileEntry.file((file: File) => {
                    this.uploadFile(file);
                });
            } else {
                // It was a directory (empty directories are added, otherwise only files)
                // const fileEntry = droppedFile.fileEntry as FileSystemDirectoryEntry;
                // console.log(droppedFile.relativePath, fileEntry);
                this._notify.info("Arquivo inválido. Somente imagens (.jpg, .png) são permitidas.");
            }
        }
    }

    upload(file: File): Observable<UploadTemporalFileResponse> {
        const formData: FormData = new FormData();
        formData.append("file", file, file.name);
        let url = this.baseUrl + "/api/services/app/TemporalFileManagerService/Upload";

        let options_: any = {
            observe: "response",
            responseType: "blob",
        };

        return this._httpClient
            .post(url, formData, options_)
            .pipe(
                _observableMergeMap((response_: any) => {
                    return this.processUpload(response_);
                })
            )
            .pipe(
                _observableCatch((response_: any) => {
                    if (response_ instanceof HttpResponseBase) {
                        try {
                            return this.processUpload(<any>response_);
                        } catch (e) {
                            return <Observable<UploadTemporalFileResponse>>(<any>_observableThrow(e));
                        }
                    } else return <Observable<UploadTemporalFileResponse>>(<any>_observableThrow(response_));
                })
            );
    }

    uploadBlob(blob: Blob, originalFile?: string | undefined): Observable<UploadTemporalFileResponse> {
        const formData: FormData = new FormData();
        formData.append("file", blob);
        if (originalFile) {
            formData.append("originalFile", originalFile);
        }
        let url = this.baseUrl + "/api/services/app/TemporalFileManagerService/Upload";

        let options_: any = {
            observe: "response",
            responseType: "blob",
        };

        return this._httpClient
            .post(url, formData, options_)
            .pipe(
                _observableMergeMap((response_: any) => {
                    return this.processUpload(response_);
                })
            )
            .pipe(
                _observableCatch((response_: any) => {
                    if (response_ instanceof HttpResponseBase) {
                        try {
                            return this.processUpload(<any>response_);
                        } catch (e) {
                            return <Observable<UploadTemporalFileResponse>>(<any>_observableThrow(e));
                        }
                    } else return <Observable<UploadTemporalFileResponse>>(<any>_observableThrow(response_));
                })
            );
    }

    uploadFile(file) {
        this.uploadingImage = true;
        this.imageUploaded = false;
        this._changeDetector.detectChanges();
        this.upload(file).subscribe(
            (response) => {
                this.imageUrl = this._sanitizer.bypassSecurityTrustUrl(response.url);
                this.plainImageUrl = response.url;
                this.imageUploaded = true;
                this._changeDetector.detectChanges();
            },
            (error1) => {
                this.uploadingImage = false;
                this._changeDetector.detectChanges();
            }
        );
    }

    exportCanvas(base64?: any, options?: Cropper.GetCroppedCanvasOptions) {
        // Get and set image, crop and canvas data
        let cropper = this.angularCropper.cropper;
        const imageData = cropper.getImageData();
        const cropData = cropper.getCropBoxData();

        if (this.settings) {
            Object.assign(options, {
                width: this.settings.width,
                minWidth: this.settings.width,
                maxWidth: this.settings.width,
                height: this.settings.height,
                minHeight: this.settings.height,
                maxHeight: this.settings.height,
            });
        }

        const canvas = cropper.getCroppedCanvas(options);
        const data = { imageData, cropData };

        // Create promise to resolve canvas data
        const promise = new Promise((resolve) => {
            //
            // Validate base64
            if (base64) {
                //
                // Resolve promise with dataUrl
                return resolve({
                    dataUrl: canvas.toDataURL("image/png"),
                });
            }
            canvas.toBlob((blob) => resolve({ blob }));
        });

        // Emit export data when promise is ready
        promise.then((res) => {
            this.export.emit(Object.assign(data, res));
        });
    }

    protected processUpload(response: HttpResponseBase): Observable<UploadTemporalFileResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse
                ? response.body
                : (<any>response).error instanceof Blob
                ? (<any>response).error
                : undefined;

        let _headers: any = {};
        if (response.headers) {
            for (let key of response.headers.keys()) {
                _headers[key] = response.headers.get(key);
            }
        }
        if (status === 200) {
            return ImageEditorDialogComponent.blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    let result200: any = null;
                    let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
                    result200 = resultData200
                        ? UploadTemporalFileResponse.fromJS(resultData200)
                        : new UploadTemporalFileResponse();
                    return _observableOf(result200);
                })
            );
        } else if (status === 401) {
            return ImageEditorDialogComponent.blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    return ImageEditorDialogComponent.throwException(
                        "A server error occurred.",
                        status,
                        _responseText,
                        _headers
                    );
                })
            );
        } else if (status !== 200 && status !== 204) {
            return ImageEditorDialogComponent.blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    return ImageEditorDialogComponent.throwException(
                        "An unexpected server error occurred.",
                        status,
                        _responseText,
                        _headers
                    );
                })
            );
        }
        return _observableOf<UploadTemporalFileResponse>(<any>null);
    }

    static blobToText(blob: any): Observable<string> {
        return new Observable<string>((observer: any) => {
            if (!blob) {
                observer.next("");
                observer.complete();
            } else {
                let reader = new FileReader();
                reader.onload = function () {
                    observer.next(this.result);
                    observer.complete();
                };
                reader.readAsText(blob);
            }
        });
    }

    static throwException(
        message: string,
        status: number,
        response: string,
        headers: { [key: string]: any },
        result?: any
    ): Observable<any> {
        if (result !== null && result !== undefined) return _observableThrow(result);
        else return _observableThrow(new ApiException(message, status, response, headers, null));
    }

    public fileOver(event) {}

    public fileLeave(event) {}
}
